import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { StorageAdapter, ExportData } from '@trackmind/storage';
import { createStorage } from '@trackmind/storage';
import { clearFinanceCache } from '@/lib/finance-cache';
import { describeSyncSetupError } from '@/lib/sync-errors';
import {
  SyncEngine,
  GitHubAPI,
  GitHubAuth,
  GoogleAuth,
  GoogleDriveAPI,
  parseCallbackParams,
  parseGoogleCallbackParams,
  type SyncStatus,
  type SyncResult,
  type AuthState,
  type GoogleAuthState,
  type UnifiedAuthState,
} from '@trackmind/sync';
import type {
  Task,
  Project,
  Schedule,
  Goal,
  Note,
  TaskList,
  ListInstance,
  HabitInstance,
  JournalEntry,
  Review,
  FitnessWorkout,
  FitnessBodyweight,
  FitnessSettings,
  UserPreferences,
  UserStats,
} from '@trackmind/core';
import { createDefaultPreferences,
  detectTimezone, createDefaultStats, normalizeFitnessSettings, todayIn, shiftDateKey, activityFacts, activeDates,
  pruneOrphanedSlots } from '@trackmind/core';
import { trackAuth, trackSessionStart, trackFeatureUse, setUserId } from '@/lib/analytics';

// Auth provider type
export type AuthProvider = 'github' | 'google' | null;

interface AppContextValue {
  // Storage
  storage: StorageAdapter | null;
  isInitialized: boolean;
  initError: string | null;

  // Auth
  authProvider: AuthProvider;
  authState: UnifiedAuthState | null;
  isGitHubAvailable: boolean;
  isGoogleAvailable: boolean;
  isGitHubConnected: boolean;
  isGoogleConnected: boolean;
  loginWithGitHub: () => void;
  loginWithGoogle: () => Promise<void>;
  logout: () => void;

  // Sync
  syncEngine: SyncEngine | null;
  syncStatus: SyncStatus;
  lastSyncTime: Date | null;
  pendingChanges: number;
  syncWarning: string | null;
  dismissSyncWarning: () => void;
  sync: () => Promise<SyncResult | null>;
  forcePush: () => Promise<SyncResult | null>;
  forcePull: () => Promise<SyncResult | null>;

  // Data - Explicit Folders (separate for notes and journals)
  noteFolders: string[];
  addNoteFolder: (path: string) => void;
  deleteNoteFolder: (path: string) => void;
  journalFolders: string[];
  addJournalFolder: (path: string) => void;
  deleteJournalFolder: (path: string) => void;

  // Data - Tasks
  tasks: Task[];
  loadTasks: () => Promise<void>;
  saveTask: (task: Task) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;

  // Data - Projects
  projects: Project[];
  loadProjects: () => Promise<void>;
  saveProject: (project: Project) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  // Data - Schedules
  schedules: Map<string, Schedule>;
  loadSchedule: (date: string) => Promise<Schedule | null>;
  loadSchedules: (startDate: string, endDate: string) => Promise<void>;
  saveSchedule: (schedule: Schedule) => Promise<void>;
  saveSchedules: (schedules: Schedule[]) => Promise<void>;

  // Data - Goals
  goals: Goal[];
  loadGoals: () => Promise<void>;
  saveGoal: (goal: Goal) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;

  // Data - Notes
  notes: Note[];
  loadNotes: () => Promise<void>;
  saveNote: (note: Note) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;

  // Data - Lists
  lists: TaskList[];
  loadLists: () => Promise<void>;
  saveList: (list: TaskList) => Promise<void>;
  deleteList: (id: string) => Promise<void>;

  // Data - List Instances
  listInstances: ListInstance[];
  loadListInstances: () => Promise<void>;
  saveListInstance: (instance: ListInstance) => Promise<void>;
  deleteListInstance: (id: string) => Promise<void>;

  // Data - Habit Instances
  /** Full completion history, in memory. See the state declaration for why. */
  habitInstances: HabitInstance[];
  loadHabitInstances: () => Promise<void>;
  getHabitInstance: (taskId: string, date: string) => Promise<HabitInstance | null>;
  getHabitInstances: (date: string) => Promise<HabitInstance[]>;
  getHabitInstancesByHabit: (habitId: string, startDate?: string, endDate?: string) => Promise<HabitInstance[]>;
  saveHabitInstance: (instance: HabitInstance) => Promise<void>;
  /** Undo one habit tick. The instance IS the completion, so this removes it. */
  deleteHabitInstance: (habitId: string, date: string) => Promise<void>;

  // Data - Journal Entries
  journalEntries: JournalEntry[];
  loadJournalEntries: () => Promise<void>;
  saveJournalEntry: (entry: JournalEntry) => Promise<void>;
  deleteJournalEntry: (id: string) => Promise<void>;
  getJournalEntriesByDate: (date: string) => Promise<JournalEntry[]>;
  getJournalEntriesByDateRange: (startDate: string, endDate: string) => Promise<JournalEntry[]>;

  // Data - Reviews. Persisted since v1.54: they used to live in useReviews'
  // local state, so the notes a user wrote were lost on reload.
  reviews: Review[];
  loadReviews: () => Promise<void>;
  saveReview: (review: Review) => Promise<void>;
  deleteReview: (id: string) => Promise<void>;

  // Data - Fitness (v2.5). The workout log is the source of truth; next
  // weights, streaks and records are derived from it in core.
  fitnessWorkouts: FitnessWorkout[];
  fitnessBodyweights: FitnessBodyweight[];
  /** Always populated — defaults until the user changes something. */
  fitnessSettings: FitnessSettings;
  saveFitnessWorkout: (workout: FitnessWorkout) => Promise<void>;
  deleteFitnessWorkout: (id: string) => Promise<void>;
  saveFitnessBodyweight: (entry: FitnessBodyweight) => Promise<void>;
  deleteFitnessBodyweight: (id: string) => Promise<void>;
  saveFitnessSettings: (settings: FitnessSettings) => Promise<void>;

  // Data - Preferences & Stats
  preferences: UserPreferences;
  loadPreferences: () => Promise<void>;
  savePreferences: (prefs: UserPreferences) => Promise<void>;
  stats: UserStats;
  loadStats: () => Promise<void>;
  saveStats: (stats: UserStats) => Promise<void>;
  recomputeStreak: () => Promise<UserStats>;

  // Utility
  refreshAll: () => Promise<void>;

  // Import/Export
  exportData: () => Promise<ExportData | null>;
  switchProvider: (target: 'github' | 'google') => Promise<{ financeFilesMoved: number } | null>;
  importData: (data: ExportData) => Promise<void>;
  clearAllData: () => Promise<void>;

  // Raw GitHub file access (used by Finance feature)
  readRawFile: (path: string) => Promise<string | null>;
  writeRawFile: (path: string, content: string, message: string) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

function generateDeviceId(): string {
  const existing = localStorage.getItem('trackmind_device_id');
  if (existing) return existing;

  const id = `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  localStorage.setItem('trackmind_device_id', id);
  return id;
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  // Core state
  const [storage, setStorage] = useState<StorageAdapter | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  // Auth state - support both providers
  const [githubAuth, setGithubAuth] = useState<GitHubAuth | null>(null);
  const [googleAuth, setGoogleAuth] = useState<GoogleAuth | null>(null);
  const [authProvider, setAuthProvider] = useState<AuthProvider>(null);
  const [authState, setAuthState] = useState<UnifiedAuthState | null>(null);

  // Sync state
  const [syncEngine, setSyncEngine] = useState<SyncEngine | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [pendingChanges, setPendingChanges] = useState(0);
  const [syncWarning, setSyncWarning] = useState<string | null>(null);

  // Debounced sync timer ref (30 seconds after last change)
  const debouncedSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Forward-reference to refreshAll (defined later in this component).
  // Auto-sync triggers run BEFORE refreshAll exists in source order, so
  // they read it through this ref instead of capturing a stale closure.
  // Without this, tab-visibility / debounced syncs would update IndexedDB
  // silently but never re-read into React state — the user would see
  // stale UI even though sync had actually run.
  const refreshAllRef = useRef<(() => Promise<void>) | null>(null);

  // Data state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [schedules, setSchedules] = useState<Map<string, Schedule>>(new Map());
  const [goals, setGoals] = useState<Goal[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [lists, setLists] = useState<TaskList[]>([]);
  const [listInstances, setListInstances] = useState<ListInstance[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [fitnessWorkouts, setFitnessWorkouts] = useState<FitnessWorkout[]>([]);
  const [fitnessBodyweights, setFitnessBodyweights] = useState<FitnessBodyweight[]>([]);
  const [fitnessSettings, setFitnessSettings] = useState<FitnessSettings>(() => normalizeFitnessSettings(null));
  // Habit completions live here rather than being queried per-date, because a
  // habit task's own status never says "done" — these records are the only
  // completion history, and every consumer that couldn't reach them
  // synchronously ended up reporting zero instead.
  const [habitInstances, setHabitInstances] = useState<HabitInstance[]>([]);
  const [preferences, setPreferences] = useState<UserPreferences>(createDefaultPreferences());
  const [stats, setStats] = useState<UserStats>(createDefaultStats());

  // Note folders (persisted to localStorage)
  const [noteFolders, setNoteFolders] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('trackmind_note_folders');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Journal folders (persisted to localStorage)
  const [journalFolders, setJournalFolders] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('trackmind_journal_folders');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persist note folders to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('trackmind_note_folders', JSON.stringify(noteFolders));
  }, [noteFolders]);

  // Persist journal folders to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('trackmind_journal_folders', JSON.stringify(journalFolders));
  }, [journalFolders]);

  const addNoteFolder = useCallback((path: string) => {
    const normalizedPath = path.trim();
    if (!normalizedPath) return;
    setNoteFolders(prev => {
      if (prev.includes(normalizedPath)) return prev;
      return [...prev, normalizedPath].sort();
    });
  }, []);

  const deleteNoteFolder = useCallback((path: string) => {
    setNoteFolders(prev => prev.filter(f => f !== path && !f.startsWith(path + '/')));
  }, []);

  const addJournalFolder = useCallback((path: string) => {
    const normalizedPath = path.trim();
    if (!normalizedPath) return;
    setJournalFolders(prev => {
      if (prev.includes(normalizedPath)) return prev;
      return [...prev, normalizedPath].sort();
    });
  }, []);

  const deleteJournalFolder = useCallback((path: string) => {
    setJournalFolders(prev => prev.filter(f => f !== path && !f.startsWith(path + '/')));
  }, []);

  // Helper to create unified auth state
  const createUnifiedAuthState = (
    provider: 'github' | 'google',
    githubState?: AuthState | null,
    googleState?: GoogleAuthState | null
  ): UnifiedAuthState | null => {
    if (provider === 'github' && githubState?.isAuthenticated && githubState.user) {
      return {
        isAuthenticated: true,
        provider: 'github',
        user: {
          id: githubState.user.login,
          displayName: githubState.user.name,
          email: githubState.user.email,
          avatarUrl: githubState.user.avatarUrl,
          provider: 'github',
        },
        expiresAt: githubState.expiresAt,
      };
    }
    if (provider === 'google' && googleState?.isAuthenticated && googleState.user) {
      return {
        isAuthenticated: true,
        provider: 'google',
        user: {
          id: googleState.user.id,
          displayName: googleState.user.name,
          email: googleState.user.email,
          avatarUrl: googleState.user.avatarUrl,
          provider: 'google',
        },
        expiresAt: googleState.expiresAt,
      };
    }
    return null;
  };

  // Helper to setup GitHub sync engine
  const setupGitHubSync = async (storageInstance: StorageAdapter, token: string, login: string) => {
    console.log('[Sync] Setting up GitHub sync for user:', login);
    const provider = new GitHubAPI({
      token,
      owner: login,
      repo: 'trackmind-data',
    });
    const engine = new SyncEngine(storageInstance, provider, {
      deviceId: generateDeviceId(),
    });
    setSyncEngine(engine);
    engine.onStatusChange((status) => {
      console.log('[Sync] Status changed:', status);
      setSyncStatus(status);
    });

    // Initialize the engine - this creates the repo if it doesn't exist
    try {
      console.log('[Sync] Initializing sync engine (will create repo if needed)...');
      await engine.initialize();
      console.log('[Sync] Sync engine initialized successfully');

      // Start auto-sync (every 5 minutes)
      engine.startAutoSync();
      console.log('[Sync] Auto-sync enabled (5 minute interval)');
    } catch (error) {
      console.error('[Sync] Failed to initialize sync engine:', error);
      // Non-blocking, and translated: the raw text here is the GitHub API's
      // own JSON, which is no use to the person reading the banner.
      setSyncWarning(describeSyncSetupError(error, 'github').message);
    }
  };

  // Helper to setup Google Drive sync. Uses THE SAME SyncEngine as GitHub —
  // Google gets merge, tombstones, pending-change clearing, auto-sync, and
  // raw finance files through the shared CloudStorageProvider interface.
  // (The old implementation hand-rolled a non-merging overwrite inline here;
  // the second device to sync destroyed the first one's data.)
  const setupGoogleDriveSync = async (storageInstance: StorageAdapter, googleAuthInstance: GoogleAuth) => {
    console.log('[Sync] Setting up Google Drive sync');
    const provider = new GoogleDriveAPI({
      getAccessToken: async () => {
        if (googleAuthInstance.needsRefresh()) {
          await googleAuthInstance.refreshAccessToken();
        }
        const token = googleAuthInstance.getToken();
        if (!token) {
          // Never hand back a known-bad token — surface the expiry instead.
          throw new Error('Google session expired. Please sign in again.');
        }
        return token;
      },
    });
    const engine = new SyncEngine(storageInstance, provider, {
      deviceId: generateDeviceId(),
    });
    setSyncEngine(engine);
    engine.onStatusChange((status) => {
      console.log('[Sync] Status changed:', status);
      setSyncStatus(status);
    });

    try {
      console.log('[Sync] Initializing sync engine (will create Drive folder if needed)...');
      await engine.initialize();
      console.log('[Sync] Sync engine initialized successfully');
      engine.startAutoSync();
    } catch (error) {
      console.error('[Sync] Failed to initialize sync engine:', error);
      setSyncWarning(describeSyncSetupError(error, 'google').message);
    }
  };

  // Initialize storage and auth
  useEffect(() => {
    async function init() {
      try {
        const storageInstance = createStorage('indexeddb');
        await storageInstance.initialize();
        setStorage(storageInstance);

        // Initialize both auth providers if configured
        let githubAuthInstance: GitHubAuth | null = null;
        let googleAuthInstance: GoogleAuth | null = null;

        // Try GitHub auth
        try {
          const githubClientId = import.meta.env.VITE_GITHUB_CLIENT_ID;
          if (githubClientId) {
            githubAuthInstance = GitHubAuth.createFromEnv();
            setGithubAuth(githubAuthInstance);
          }
        } catch (e) {
          console.warn('GitHub auth not configured:', e);
        }

        // Try Google auth
        try {
          const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
          if (googleClientId) {
            googleAuthInstance = GoogleAuth.createFromEnv();
            setGoogleAuth(googleAuthInstance);
          }
        } catch (e) {
          console.warn('Google auth not configured:', e);
        }

        // Check for OAuth callbacks
        const url = window.location.href;

        // Check for Google callback first (has scope in URL)
        const googleParams = parseGoogleCallbackParams(url);
        if (googleParams && googleAuthInstance) {
          try {
            await googleAuthInstance.handleCallback(googleParams.code, googleParams.state);
            window.history.replaceState({}, '', window.location.pathname);

            // Set up Google Drive sync
            const googleUserState = googleAuthInstance.getAuthState();
            setAuthProvider('google');
            setAuthState(createUnifiedAuthState('google', null, googleUserState));
            await setupGoogleDriveSync(storageInstance, googleAuthInstance);
            if (googleUserState.user?.id) {
              setUserId(googleUserState.user.id);
            }
            trackAuth('google');
          } catch (callbackError) {
            console.error('Google OAuth callback failed:', callbackError);
            window.history.replaceState({}, '', window.location.pathname);
          }
        }
        // Check for GitHub callback
        else {
          const githubParams = parseCallbackParams(url);
          if (githubParams && githubAuthInstance) {
            console.log('[Auth] GitHub callback detected, processing...');
            try {
              await githubAuthInstance.handleCallback(githubParams.code, githubParams.state);
              console.log('[Auth] GitHub callback handled successfully');
              window.history.replaceState({}, '', window.location.pathname);

              // Set up GitHub sync
              const ghState = githubAuthInstance.getAuthState();
              console.log('[Auth] GitHub auth state:', ghState.isAuthenticated, ghState.user?.login);
              if (ghState.user) {
                setAuthProvider('github');
                setAuthState(createUnifiedAuthState('github', ghState, null));
                console.log('[Auth] Setting up GitHub sync...');
                await setupGitHubSync(storageInstance, ghState.token!, ghState.user.login);
                console.log('[Auth] GitHub sync setup complete');
                setUserId(ghState.user.login);
                trackAuth('github');
              }
            } catch (callbackError) {
              console.error('[Auth] GitHub OAuth callback failed:', callbackError);
              window.history.replaceState({}, '', window.location.pathname);
              // Show error to user
              const errorMessage = callbackError instanceof Error ? callbackError.message : 'Authentication failed';
              alert(`GitHub sign-in failed: ${errorMessage}\n\nPlease try again.`);
            }
          }
        }

        // If no callback, check for existing auth from storage
        if (!googleParams && !parseCallbackParams(url)) {
          console.log('[Auth] No callback params, checking stored auth...');
          console.log('[Auth] Google auth available:', !!googleAuthInstance, 'authenticated:', googleAuthInstance?.isAuthenticated());
          console.log('[Auth] GitHub auth available:', !!githubAuthInstance, 'authenticated:', githubAuthInstance?.isAuthenticated());

          // Check Google first
          if (googleAuthInstance?.isAuthenticated()) {
            console.log('[Auth] Restoring Google auth from storage');
            const googleUserState = googleAuthInstance.getAuthState();
            setAuthProvider('google');
            setAuthState(createUnifiedAuthState('google', null, googleUserState));
            await setupGoogleDriveSync(storageInstance, googleAuthInstance);
            if (googleUserState.user?.id) {
              setUserId(googleUserState.user.id);
            }
            trackSessionStart('google');
          }
          // Then check GitHub
          else if (githubAuthInstance?.isAuthenticated()) {
            const ghState = githubAuthInstance.getAuthState();
            console.log('[Auth] Restoring GitHub auth from storage, user:', ghState.user?.login);
            if (ghState.user && ghState.token) {
              setAuthProvider('github');
              setAuthState(createUnifiedAuthState('github', ghState, null));
              await setupGitHubSync(storageInstance, ghState.token, ghState.user.login);
              console.log('[Auth] GitHub auth restored successfully');
              setUserId(ghState.user.login);
              trackSessionStart('github');
            }
          } else {
            console.log('[Auth] No stored auth found');
          }
        }

        // Load initial data
        const [
          loadedTasks,
          loadedProjects,
          loadedGoals,
          loadedNotes,
          loadedLists,
          loadedInstances,
          loadedJournalEntries,
          loadedHabitInstances,
          // Loaded at boot, not lazily: review ids are deterministic, so any
          // consumer that generates against an empty list writes a fresh
          // review over the stored one — erasing its notes. The reviews must
          // be in memory before isInitialized turns true, because that flag is
          // what tells persistence it's safe to write.
          loadedReviews,
          // At boot for the same reason as reviews: the settings row has a
          // fixed id, so writing defaults over a not-yet-loaded row would
          // replace the user's real settings.
          loadedFitnessWorkouts,
          loadedFitnessBodyweights,
          loadedFitnessSettings,
          loadedPrefs,
          loadedStats,
          loadedLastSync,
        ] = await Promise.all([
          storageInstance.getTasks(),
          storageInstance.getProjects(),
          storageInstance.getGoals(),
          storageInstance.getNotes(),
          storageInstance.getLists(),
          storageInstance.getListInstances(),
          storageInstance.getJournalEntries(),
          storageInstance.getAllHabitInstances(),
          storageInstance.getReviews(),
          storageInstance.getFitnessWorkouts(),
          storageInstance.getFitnessBodyweights(),
          storageInstance.getFitnessSettings(),
          storageInstance.getPreferences(),
          storageInstance.getStats(),
          storageInstance.getLastSyncTime(),
        ]);

        setTasks(loadedTasks);
        setProjects(loadedProjects);
        setGoals(loadedGoals);
        setNotes(loadedNotes);
        setLists(loadedLists);
        setListInstances(loadedInstances);
        setJournalEntries(loadedJournalEntries);
        setHabitInstances(loadedHabitInstances);
        setReviews(loadedReviews);
        setFitnessWorkouts(loadedFitnessWorkouts);
        setFitnessBodyweights(loadedFitnessBodyweights);
        setFitnessSettings(normalizeFitnessSettings(loadedFitnessSettings));
        /**
         * Stamp the device's time zone onto the user's preferences.
         *
         * Not a setting — a fact the browser already knows, written so that
         * something OTHER than this browser can answer "what day is it for
         * this person". The MCP server runs in UTC, and without this it
         * answered "what's on today" with tomorrow's habits for every hour
         * after 5pm local.
         *
         * Written only when it actually differs, so it does not mark the
         * preferences dirty and trigger a sync on every single app load. It
         * updates itself when the user travels, which is the behaviour you
         * want and the reason it is not asked for once at onboarding.
         */
        const zone = detectTimezone();
        // Never over a deliberate choice. A setting that silently resets on
        // the next load is worse than no setting, because the user stops
        // trusting it — and they would be right to.
        const chosen = loadedPrefs.timezone_source === 'manual';
        const zoneMoved = !chosen && zone && loadedPrefs.timezone !== zone;

        /**
         * Record which build wrote this data, the same way and for the same
         * reason as the time zone: written only when it CHANGES, so an
         * ordinary load does not mark preferences dirty and trigger a sync.
         *
         * Without it nothing anywhere could say which version is actually
         * running — the export's `version` is the data format, frozen at
         * 1.0.0. That matters for a PWA, which keeps serving the cached build
         * until the user taps Update: "is the fix live for me yet" had no
         * answer, in the app or through the assistant.
         */
        const versionMoved = loadedPrefs.app_version !== __APP_VERSION__;

        if (zoneMoved || versionMoved) {
          const stamped = {
            ...loadedPrefs,
            ...(zoneMoved ? { timezone: zone, timezone_source: 'auto' as const } : {}),
            ...(versionMoved
              ? { app_version: __APP_VERSION__, app_version_since: new Date().toISOString() }
              : {}),
          };
          setPreferences(stamped);
          void storageInstance.savePreferences(stamped).catch((error) => {
            // A failure here costs a correct date for the assistant, not the
            // app — never enough to interrupt a load.
            console.warn('[Preferences] Could not record time zone / version:', error);
          });
        } else {
          setPreferences(loadedPrefs);
        }
        setStats(loadedStats);
        setLastSyncTime(loadedLastSync);

        // Update pending changes count
        const changes = await storageInstance.getPendingChanges();
        setPendingChanges(changes.length);

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize storage:', error);
        setInitError(error instanceof Error ? error.message : 'Unknown error');
      }
    }

    init();
  }, []);

  // Visibility change listener - sync when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // User returned to this tab - sync if needed
        if (syncEngine) {
          console.log('[Sync] Tab became visible - checking if sync needed');
          try {
            const ran = await syncEngine.syncIfNeeded();
            // syncIfNeeded silently updates IndexedDB but doesn't notify
            // React. Refresh local state from storage so the UI reflects
            // what just got pulled — otherwise the user sees stale data
            // even though sync ran successfully.
            if (ran && refreshAllRef.current) {
              await refreshAllRef.current();
            }
          } catch (error) {
            console.error('[Sync] Visibility sync failed:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authProvider, syncEngine]);

  // Cleanup debounced sync timer on unmount
  useEffect(() => {
    return () => {
      if (debouncedSyncTimerRef.current) {
        clearTimeout(debouncedSyncTimerRef.current);
      }
    };
  }, []);

  // Auth handlers
  const loginWithGitHub = useCallback(() => {
    if (!githubAuth) {
      alert('GitHub sign-in is not configured. Please contact the administrator or use Google sign-in.');
      return;
    }
    githubAuth.startOAuthFlow();
  }, [githubAuth]);

  const loginWithGoogle = useCallback(async () => {
    if (!googleAuth) {
      alert('Google sign-in is not configured. Please contact the administrator or use GitHub sign-in.');
      return;
    }
    await googleAuth.startOAuthFlow();
  }, [googleAuth]);

  const logout = useCallback(() => {
    // Logout from current provider
    if (authProvider === 'github') {
      githubAuth?.logout();
    } else if (authProvider === 'google') {
      googleAuth?.logout();
    }
    setAuthState(null);
    setAuthProvider(null);
    setSyncEngine(null);
    setSyncStatus('idle');
  }, [authProvider, githubAuth, googleAuth]);

  // Debounced sync - triggers 30 seconds after last change
  // This batches multiple rapid changes into a single sync
  const triggerDebouncedSync = useCallback(() => {
    // Works for any provider — the engine is provider-agnostic
    if (!syncEngine) {
      return;
    }

    // Clear any existing timer
    if (debouncedSyncTimerRef.current) {
      clearTimeout(debouncedSyncTimerRef.current);
    }

    // Set new timer for 30 seconds
    debouncedSyncTimerRef.current = setTimeout(async () => {
      console.log('[Sync] Debounced sync triggered after 30s of inactivity');
      try {
        const ran = await syncEngine.syncIfNeeded();
        // Same reason as in handleVisibilityChange — IndexedDB is
        // updated by sync but React state isn't notified unless we
        // explicitly refresh.
        if (ran && refreshAllRef.current) {
          await refreshAllRef.current();
        }
      } catch (error) {
        console.error('[Sync] Debounced sync failed:', error);
      }
    }, 30 * 1000); // 30 seconds
  }, [authProvider, syncEngine]);

  // Sync handlers - supports both GitHub (via SyncEngine) and Google Drive (direct API)
  const sync = useCallback(async (): Promise<SyncResult | null> => {
    if (!storage) return null;

    // Both providers go through the same SyncEngine (merge, tombstones,
    // pending-change clearing) — only the injected storage backend differs.
    if (syncEngine) {
      const result = await syncEngine.sync();
      if (result.success) {
        setLastSyncTime(new Date());
        setPendingChanges(0);
        await refreshAll();
        trackFeatureUse('sync_manual');
      }
      return result;
    }

    return null;
  }, [syncEngine, storage]);

  const forcePush = useCallback(async (): Promise<SyncResult | null> => {
    if (syncEngine) {
      return syncEngine.sync({ forceDirection: 'push' });
    }
    return sync();
  }, [syncEngine, sync]);

  const forcePull = useCallback(async (): Promise<SyncResult | null> => {
    if (!storage) return null;

    if (syncEngine) {
      const result = await syncEngine.sync({ forceDirection: 'pull' });
      if (result.success) {
        await refreshAll();
      }
      return result;
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncEngine, storage]);

  // Task handlers
  const loadTasks = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getTasks();
    setTasks(loaded);
  }, [storage]);

  const saveTask = useCallback(async (task: Task) => {
    if (!storage) return;

    // Check if this is a new task (for analytics)
    const isNew = !tasks.some(t => t.id === task.id);

    await storage.saveTask(task);
    setTasks(prev => {
      const existing = prev.findIndex(t => t.id === task.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = task;
        return updated;
      }
      return [...prev, task];
    });
    const changes = await storage.getPendingChanges();
    setPendingChanges(changes.length);
    triggerDebouncedSync();

    // Track analytics for new tasks only (not habits - habits are tracked separately)
    if (isNew && !task.is_habit) {
      trackFeatureUse('task_create');
    }
  }, [storage, tasks, triggerDebouncedSync]);

  const deleteTask = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteTask(id);
    setTasks(prev => prev.filter(t => t.id !== id));
    // Storage deleted the habit's completion history with it.
    setHabitInstances(prev => prev.filter(i => i.habit_id !== id));
    // And its slots on any day's plan — core's pruneOrphanedSlots, which the
    // assistant already runs on every write. Left behind, each one showed as
    // an "Unknown Task" row on that day, on every device (found by
    // e2e/two-devices-test). Only this id is passed as provably deleted, so
    // nothing else on a plan can be touched.
    try {
      const all = await storage.getSchedules('0000-01-01', '9999-12-31');
      const pruned = pruneOrphanedSlots(all, [], new Set([id]));
      const changed = pruned.schedules.filter((s) => pruned.dates.includes(s.date));
      if (changed.length > 0) {
        for (const schedule of changed) await storage.saveSchedule(schedule);
        setSchedules(prev => {
          const next = new Map(prev);
          for (const schedule of changed) if (next.has(schedule.date)) next.set(schedule.date, schedule);
          return next;
        });
      }
    } catch (error) {
      // A plan that couldn't be tidied is a cosmetic ghost, not a failed delete.
      console.warn('[deleteTask] could not clear plan slots:', error);
    }
    const changes = await storage.getPendingChanges();
    setPendingChanges(changes.length);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Project handlers
  const loadProjects = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getProjects();
    setProjects(loaded);
  }, [storage]);

  const saveProject = useCallback(async (project: Project) => {
    if (!storage) return;
    const isNew = !projects.some(p => p.id === project.id);
    await storage.saveProject(project);
    setProjects(prev => {
      const existing = prev.findIndex(p => p.id === project.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = project;
        return updated;
      }
      return [...prev, project];
    });
    triggerDebouncedSync();
    if (isNew) {
      trackFeatureUse('project_create');
    }
  }, [storage, projects, triggerDebouncedSync]);

  const deleteProject = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteProject(id);
    setProjects(prev => prev.filter(p => p.id !== id));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Schedule handlers
  const loadSchedule = useCallback(async (date: string): Promise<Schedule | null> => {
    if (!storage) return null;
    const schedule = await storage.getSchedule(date);
    if (schedule) {
      setSchedules(prev => new Map(prev).set(date, schedule));
    }
    return schedule;
  }, [storage]);

  const loadSchedules = useCallback(async (startDate: string, endDate: string) => {
    if (!storage) return;
    const loaded = await storage.getSchedules(startDate, endDate);
    setSchedules(prev => {
      const updated = new Map(prev);
      for (const schedule of loaded) {
        updated.set(schedule.date, schedule);
      }
      return updated;
    });
  }, [storage]);

  const saveSchedule = useCallback(async (schedule: Schedule) => {
    if (!storage) return;
    await storage.saveSchedule(schedule);
    setSchedules(prev => new Map(prev).set(schedule.date, schedule));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  const saveSchedules = useCallback(async (scheduleList: Schedule[]) => {
    if (!storage) return;
    await storage.saveSchedules(scheduleList);
    setSchedules(prev => {
      const updated = new Map(prev);
      for (const schedule of scheduleList) {
        updated.set(schedule.date, schedule);
      }
      return updated;
    });
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Goal handlers
  const loadGoals = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getGoals();
    setGoals(loaded);
  }, [storage]);

  const saveGoal = useCallback(async (goal: Goal) => {
    if (!storage) return;
    const isNew = !goals.some(g => g.id === goal.id);
    await storage.saveGoal(goal);
    setGoals(prev => {
      const existing = prev.findIndex(g => g.id === goal.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = goal;
        return updated;
      }
      return [...prev, goal];
    });
    triggerDebouncedSync();
    if (isNew) {
      trackFeatureUse('goal_create');
    }
  }, [storage, goals, triggerDebouncedSync]);

  const deleteGoal = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteGoal(id);
    setGoals(prev => prev.filter(g => g.id !== id));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Note handlers
  const loadNotes = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getNotes();
    setNotes(loaded);
  }, [storage]);

  const saveNote = useCallback(async (note: Note) => {
    if (!storage) return;
    const isNew = !notes.some(n => n.id === note.id);
    await storage.saveNote(note);
    setNotes(prev => {
      const existing = prev.findIndex(n => n.id === note.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = note;
        return updated;
      }
      return [...prev, note];
    });
    triggerDebouncedSync();
    if (isNew) {
      trackFeatureUse('note_create');
    }
  }, [storage, notes, triggerDebouncedSync]);

  const deleteNote = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteNote(id);
    setNotes(prev => prev.filter(n => n.id !== id));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // List handlers
  const loadLists = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getLists();
    setLists(loaded);
  }, [storage]);

  const saveList = useCallback(async (list: TaskList) => {
    if (!storage) return;
    const isNew = !lists.some(l => l.id === list.id);
    await storage.saveList(list);
    setLists(prev => {
      const existing = prev.findIndex(l => l.id === list.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = list;
        return updated;
      }
      return [...prev, list];
    });
    triggerDebouncedSync();
    if (isNew) {
      trackFeatureUse('list_create');
    }
  }, [storage, lists, triggerDebouncedSync]);

  const deleteList = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteList(id);
    setLists(prev => prev.filter(l => l.id !== id));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // List Instance handlers
  const loadListInstances = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getListInstances();
    setListInstances(loaded);
  }, [storage]);

  const saveListInstance = useCallback(async (instance: ListInstance) => {
    if (!storage) return;
    await storage.saveListInstance(instance);
    setListInstances(prev => {
      const existing = prev.findIndex(i => i.id === instance.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = instance;
        return updated;
      }
      return [...prev, instance];
    });
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  const deleteListInstance = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteListInstance(id);
    setListInstances(prev => prev.filter(i => i.id !== id));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Habit Instance handlers
  const getHabitInstance = useCallback(async (taskId: string, date: string): Promise<HabitInstance | null> => {
    if (!storage) return null;
    return storage.getHabitInstance(taskId, date);
  }, [storage]);

  const getHabitInstances = useCallback(async (date: string): Promise<HabitInstance[]> => {
    if (!storage) return [];
    return storage.getHabitInstances(date);
  }, [storage]);

  const getHabitInstancesByHabit = useCallback(async (habitId: string, startDate?: string, endDate?: string): Promise<HabitInstance[]> => {
    if (!storage) return [];
    return storage.getHabitInstancesByHabit(habitId, startDate, endDate);
  }, [storage]);

  const loadHabitInstances = useCallback(async () => {
    if (!storage) return;
    setHabitInstances(await storage.getAllHabitInstances());
  }, [storage]);

  const saveHabitInstance = useCallback(async (instance: HabitInstance) => {
    if (!storage) return;
    await storage.saveHabitInstance(instance);
    // Keep the in-memory copy in step, keyed the same way storage is, so a
    // re-tick of the same habit on the same day replaces rather than doubles.
    setHabitInstances(prev => {
      const next = prev.filter(
        i => !(i.habit_id === instance.habit_id && i.date === instance.date),
      );
      next.push(instance);
      return next;
    });
    triggerDebouncedSync();
    // Saving an instance IS the completion record — HabitInstance has no
    // separate completed flag.
    trackFeatureUse('habit_complete');
  }, [storage, triggerDebouncedSync]);

  const deleteHabitInstance = useCallback(async (habitId: string, date: string) => {
    if (!storage) return;
    await storage.deleteHabitInstance(habitId, date);
    // Keep the in-memory copy in step, keyed the same way storage is.
    setHabitInstances(prev => prev.filter(
      i => !(i.habit_id === habitId && i.date === date),
    ));
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Review handlers
  const loadReviews = useCallback(async () => {
    if (!storage) return;
    setReviews(await storage.getReviews());
  }, [storage]);

  const saveReview = useCallback(async (review: Review) => {
    if (!storage) return;
    await storage.saveReview(review);
    setReviews(prev => {
      const at = prev.findIndex(r => r.id === review.id);
      if (at >= 0) {
        const next = [...prev];
        next[at] = review;
        return next;
      }
      return [review, ...prev];
    });
    const changes = await storage.getPendingChanges();
    setPendingChanges(changes.length);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  const deleteReview = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteReview(id);
    setReviews(prev => prev.filter(r => r.id !== id));
    const changes = await storage.getPendingChanges();
    setPendingChanges(changes.length);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Fitness handlers. Every change itself is computed in core
  // (startWorkoutEffect, tapSetEffect, …) — these only persist the result.
  const loadFitness = useCallback(async () => {
    if (!storage) return;
    const [w, b, s] = await Promise.all([
      storage.getFitnessWorkouts(),
      storage.getFitnessBodyweights(),
      storage.getFitnessSettings(),
    ]);
    setFitnessWorkouts(w);
    setFitnessBodyweights(b);
    setFitnessSettings(normalizeFitnessSettings(s));
  }, [storage]);

  const afterFitnessWrite = useCallback(async () => {
    if (!storage) return;
    setPendingChanges((await storage.getPendingChanges()).length);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  const upsertById = <T extends { id: string }>(row: T) => (prev: T[]) => {
    const at = prev.findIndex((r) => r.id === row.id);
    if (at === -1) return [...prev, row];
    const next = [...prev];
    next[at] = row;
    return next;
  };

  const saveFitnessWorkout = useCallback(async (workout: FitnessWorkout) => {
    if (!storage) return;
    // State first: a set tap must feel instant, and the write is local.
    setFitnessWorkouts(upsertById(workout));
    await storage.saveFitnessWorkout(workout);
    await afterFitnessWrite();
  }, [storage, afterFitnessWrite]);

  const deleteFitnessWorkout = useCallback(async (id: string) => {
    if (!storage) return;
    setFitnessWorkouts(prev => prev.filter(w => w.id !== id));
    await storage.deleteFitnessWorkout(id);
    await afterFitnessWrite();
  }, [storage, afterFitnessWrite]);

  const saveFitnessBodyweight = useCallback(async (entry: FitnessBodyweight) => {
    if (!storage) return;
    setFitnessBodyweights(upsertById(entry));
    await storage.saveFitnessBodyweight(entry);
    await afterFitnessWrite();
  }, [storage, afterFitnessWrite]);

  const deleteFitnessBodyweight = useCallback(async (id: string) => {
    if (!storage) return;
    setFitnessBodyweights(prev => prev.filter(b => b.id !== id));
    await storage.deleteFitnessBodyweight(id);
    await afterFitnessWrite();
  }, [storage, afterFitnessWrite]);

  const saveFitnessSettings = useCallback(async (settings: FitnessSettings) => {
    if (!storage) return;
    setFitnessSettings(settings);
    await storage.saveFitnessSettings(settings);
    await afterFitnessWrite();
  }, [storage, afterFitnessWrite]);

  // Journal Entry handlers
  const loadJournalEntries = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getJournalEntries();
    setJournalEntries(loaded);
  }, [storage]);

  const saveJournalEntry = useCallback(async (entry: JournalEntry) => {
    if (!storage) return;
    const isNew = !journalEntries.some(e => e.id === entry.id);
    await storage.saveJournalEntry(entry);
    setJournalEntries(prev => {
      const existing = prev.findIndex(e => e.id === entry.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = entry;
        return updated;
      }
      return [...prev, entry];
    });
    const changes = await storage.getPendingChanges();
    setPendingChanges(changes.length);
    triggerDebouncedSync();
    if (isNew) {
      trackFeatureUse('journal_entry');
    }
  }, [storage, journalEntries, triggerDebouncedSync]);

  const deleteJournalEntry = useCallback(async (id: string) => {
    if (!storage) return;
    await storage.deleteJournalEntry(id);
    setJournalEntries(prev => prev.filter(e => e.id !== id));
    const changes = await storage.getPendingChanges();
    setPendingChanges(changes.length);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  const getJournalEntriesByDate = useCallback(async (date: string): Promise<JournalEntry[]> => {
    if (!storage) return [];
    return storage.getJournalEntriesByDate(date);
  }, [storage]);

  const getJournalEntriesByDateRange = useCallback(async (startDate: string, endDate: string): Promise<JournalEntry[]> => {
    if (!storage) return [];
    return storage.getJournalEntriesByDateRange(startDate, endDate);
  }, [storage]);

  // Preferences & Stats handlers
  const loadPreferences = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getPreferences();
    setPreferences(loaded);
  }, [storage]);

  const savePreferences = useCallback(async (prefs: UserPreferences) => {
    if (!storage) return;
    await storage.savePreferences(prefs);
    setPreferences(prefs);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  const loadStats = useCallback(async () => {
    if (!storage) return;
    const loaded = await storage.getStats();
    setStats(loaded);
  }, [storage]);

  const saveStats = useCallback(async (newStats: UserStats) => {
    if (!storage) return;
    await storage.saveStats(newStats);
    setStats(newStats);
    triggerDebouncedSync();
  }, [storage, triggerDebouncedSync]);

  // Rebuild current_streak and longest_streak from actual completion history.
  // Sources of truth: regular tasks with `completed_at` set, plus habit
  // instances (one per habit per completed day). The streak is the count of
  // consecutive calendar days ending today (or yesterday if nothing today)
  // that have at least one completion.
  const recomputeStreak = useCallback(async (): Promise<UserStats> => {
    if (!storage) return stats;

    // Refuse to recompute when there's nothing to compute from but the
    // stored streak claims otherwise — this is almost certainly a fresh
    // device whose sync hasn't landed yet, and clobbering the streak to
    // 0 here would propagate to remote on the next push.
    if (tasks.length === 0 && (stats.current_streak > 0 || stats.longest_streak > 0 || stats.tasks_completed > 0)) {
      console.warn('[Streak] Skipping recompute: no tasks loaded but stored streak/history is non-zero (sync may not have completed).');
      return stats;
    }

    // Core's reading of the history — the badge engine's own (activityFacts),
    // in the user's zone. This was a third private consecutive-days walk, on
    // the device clock, over instances fetched habit by habit.
    const zone = preferences?.timezone;
    const facts = activityFacts({ tasks, habitInstances, today: todayIn(zone), timezone: zone });
    const days = activeDates(tasks, habitInstances, zone);
    const lastCompletionDate = days.length > 0 ? days.reduce((a, b) => (b > a ? b : a)) : null;
    const current = facts.currentStreak;
    const fixed: UserStats = {
      ...stats,
      current_streak: current,
      longest_streak: Math.max(facts.longestStreak, current, stats.longest_streak ?? 0),
      last_completion_date: lastCompletionDate,
    };
    await storage.saveStats(fixed);
    setStats(fixed);
    triggerDebouncedSync();
    return fixed;
  }, [storage, stats, tasks, habitInstances, preferences?.timezone, triggerDebouncedSync]);

  // One-time, silent migration: rebuild streak from actual history.
  // Earlier versions of completeTask incremented current_streak on every
  // task completion instead of once per calendar day, producing wildly
  // inflated values (e.g. 361 days on a 3-month-old install). This runs
  // once per device, gated by a localStorage flag, after tasks have
  // loaded. New users with an empty history are skipped — no harm done,
  // but the flag is still set so we don't re-check on every reload.
  //
  // On a fresh device signed in to a remote sync provider, local
  // IndexedDB is empty until the initial sync completes. Running the
  // migration before that point would compute streak = 0 from the empty
  // local state, save it, and then push that zero back up — clobbering
  // the correct value on the remote. Defer until lastSyncTime is set
  // whenever a sync provider is in use.
  const streakMigrationRanRef = useRef(false);
  useEffect(() => {
    if (streakMigrationRanRef.current) return;
    if (!storage) return;
    // `storage` is set before the initial load finishes, so without this the
    // effect could fire while `stats` was still createDefaultStats() and
    // `tasks` still empty. recomputeStreak spreads `...stats`, so it then
    // SAVED those defaults — wiping total_xp, level, tasks_completed,
    // habits_completed and earned_badges, once, silently, on first load.
    // Waiting for initialisation is the whole fix: the guard below only
    // covered tasks, and a default stats record reports zero history.
    if (!isInitialized) return;
    let cancelled = false;
    try {
      if (localStorage.getItem('streak.v2.migrated') === '1') {
        streakMigrationRanRef.current = true;
        return;
      }
      // Wait for the initial sync to land on a device that pulls its
      // data from a remote provider; an empty local store is otherwise
      // indistinguishable from "no completion history".
      if ((authProvider === 'github' || authProvider === 'google') && lastSyncTime === null) return;
      // Wait until tasks have loaded. If stats say there's history but
      // tasks haven't arrived yet, defer until they do.
      if (tasks.length === 0 && stats.tasks_completed > 0) return;
      streakMigrationRanRef.current = true;
      void (async () => {
        try {
          await recomputeStreak();
          if (cancelled) return;
          localStorage.setItem('streak.v2.migrated', '1');
          console.log('[Streak migration] One-time recompute completed.');
        } catch (err) {
          console.error('[Streak migration] Failed:', err);
          streakMigrationRanRef.current = false; // allow retry next mount
        }
      })();
    } catch {
      /* localStorage unavailable — skip */
    }
    return () => { cancelled = true; };
  }, [storage, isInitialized, tasks, stats, recomputeStreak, authProvider, lastSyncTime]);

  // Refresh all data including recent schedules
  const refreshAll = useCallback(async () => {
    // Calculate date range for schedules: 30 days past to 7 days future,
    // around the user's today.
    const today = todayIn(preferences?.timezone);

    await Promise.all([
      loadTasks(),
      loadProjects(),
      loadSchedules(shiftDateKey(today, -30), shiftDateKey(today, 7)),
      loadGoals(),
      loadNotes(),
      loadLists(),
      loadListInstances(),
      loadJournalEntries(),
      loadHabitInstances(),
      loadReviews(),
      loadFitness(),
      loadPreferences(),
      loadStats(),
    ]);
  }, [loadTasks, loadProjects, loadSchedules, loadGoals, loadNotes, loadLists, loadListInstances, loadJournalEntries, loadHabitInstances, loadReviews, loadFitness, loadPreferences, loadStats, preferences?.timezone]);

  // Keep the forward-reference ref pointed at the latest refreshAll so
  // the visibility-change and debounced-sync handlers above can refresh
  // React state without taking on a circular dependency.
  useEffect(() => {
    refreshAllRef.current = refreshAll;
  }, [refreshAll]);

  // Export all data
  const exportData = useCallback(async (): Promise<ExportData | null> => {
    if (!storage) return null;
    const base = await storage.exportAll();
    // Finance lives as raw files in the cloud provider, not in local storage,
    // so exportAll() can't see it. Without this the JSON backup silently
    // dropped every transaction, investment, and paycheck.
    if (syncEngine) {
      try {
        base.finance = await syncEngine.collectFinanceFiles();
      } catch (error) {
        console.warn('[Export] Could not include finance files:', error);
      }
    }
    return base;
  }, [storage, syncEngine]);

  // Import data (merges with existing)
  const importData = useCallback(async (data: ExportData) => {
    if (!storage) return;
    await storage.importAll(data);
    if (data.finance && Object.keys(data.finance).length > 0 && syncEngine) {
      try {
        await syncEngine.restoreFinanceFiles(data.finance);
        clearFinanceCache();
      } catch (error) {
        console.warn('[Import] Could not restore finance files:', error);
      }
    }
    await refreshAll();
  }, [storage, syncEngine, refreshAll]);

  /**
   * Move all data to the other cloud provider. Authenticates the target
   * while the current session is still alive so the finance raw files (which
   * exist only in the cloud) can be copied across — the manual
   * logout/login/sync route loses them.
   */
  const switchProvider = useCallback(async (
    target: 'github' | 'google'
  ): Promise<{ financeFilesMoved: number } | null> => {
    if (!storage || !syncEngine) {
      throw new Error('Sign in before switching cloud providers');
    }
    if (syncEngine.getProviderType() === target) {
      return { financeFilesMoved: 0 };
    }

    // Push anything outstanding to the OLD provider first, so its copy stays
    // a complete fallback.
    try {
      await syncEngine.sync();
    } catch (error) {
      console.warn('[Switch] Pre-switch sync failed, continuing:', error);
    }

    let nextProvider;
    if (target === 'github') {
      const ghState = githubAuth?.getAuthState();
      if (!ghState?.isAuthenticated || !ghState.token || !ghState.user) {
        throw new Error('Connect GitHub first, then switch');
      }
      nextProvider = new GitHubAPI({
        token: ghState.token,
        owner: ghState.user.login,
        repo: 'trackmind-data',
      });
    } else {
      if (!googleAuth?.isAuthenticated()) {
        throw new Error('Connect Google first, then switch');
      }
      nextProvider = new GoogleDriveAPI({
        getAccessToken: async () => {
          if (googleAuth.needsRefresh()) {
            await googleAuth.refreshAccessToken();
          }
          const token = googleAuth.getToken();
          if (!token) throw new Error('Google session expired. Please sign in again.');
          return token;
        },
      });
    }

    const result = await syncEngine.switchProvider(nextProvider);
    setAuthProvider(target);
    const ghState = githubAuth?.getAuthState() ?? null;
    const gState = googleAuth?.getAuthState() ?? null;
    setAuthState(createUnifiedAuthState(target, ghState, gState));
    clearFinanceCache();
    await refreshAll();
    setLastSyncTime(new Date());
    return { financeFilesMoved: result.financeFilesMoved };
  }, [storage, syncEngine, githubAuth, googleAuth, refreshAll]);

  // Clear all data
  const clearAllData = useCallback(async () => {
    if (!storage) return;
    await storage.clearAll();
    setTasks([]);
    setProjects([]);
    setSchedules(new Map());
    setGoals([]);
    setNotes([]);
    setLists([]);
    setListInstances([]);
    setJournalEntries([]);
    setHabitInstances([]);
    setReviews([]);
    setFitnessWorkouts([]);
    setFitnessBodyweights([]);
    setFitnessSettings(normalizeFitnessSettings(null));
    setPreferences(createDefaultPreferences());
    setStats(createDefaultStats());
    setPendingChanges(0);
  }, [storage]);

  // Raw GitHub file access — delegates to SyncEngine which delegates to GitHubAPI.
  // Used by the Finance feature to read/write finance-YYYY.json files.
  const readRawFile = useCallback(async (path: string): Promise<string | null> => {
    if (!syncEngine) return null;
    return syncEngine.readRawFile(path);
  }, [syncEngine]);

  const writeRawFile = useCallback(async (path: string, content: string, message: string): Promise<void> => {
    if (!syncEngine) throw new Error('Not signed in — cannot sync files');
    return syncEngine.writeRawFile(path, content, message);
  }, [syncEngine]);

  const value: AppContextValue = {
    storage,
    isInitialized,
    initError,
    authProvider,
    authState,
    isGitHubAvailable: !!githubAuth,
    isGoogleAvailable: !!googleAuth,
    // "connected" = an actual authenticated session exists, as opposed to
    // "available" = OAuth is merely configured for this deployment. Switching
    // providers needs BOTH sessions live so finance files can be copied.
    isGitHubConnected: !!githubAuth?.isAuthenticated(),
    isGoogleConnected: !!googleAuth?.isAuthenticated(),
    loginWithGitHub,
    loginWithGoogle,
    logout,
    syncEngine,
    syncStatus,
    lastSyncTime,
    pendingChanges,
    syncWarning,
    dismissSyncWarning: () => setSyncWarning(null),
    sync,
    forcePush,
    forcePull,
    noteFolders,
    addNoteFolder,
    deleteNoteFolder,
    journalFolders,
    addJournalFolder,
    deleteJournalFolder,
    tasks,
    loadTasks,
    saveTask,
    deleteTask,
    projects,
    loadProjects,
    saveProject,
    deleteProject,
    schedules,
    loadSchedule,
    loadSchedules,
    saveSchedule,
    saveSchedules,
    goals,
    loadGoals,
    saveGoal,
    deleteGoal,
    notes,
    loadNotes,
    saveNote,
    deleteNote,
    lists,
    loadLists,
    saveList,
    deleteList,
    listInstances,
    loadListInstances,
    saveListInstance,
    deleteListInstance,
    habitInstances,
    loadHabitInstances,
    getHabitInstance,
    getHabitInstances,
    getHabitInstancesByHabit,
    saveHabitInstance,
    deleteHabitInstance,
    journalEntries,
    reviews,
    loadReviews,
    saveReview,
    deleteReview,
    fitnessWorkouts,
    fitnessBodyweights,
    fitnessSettings,
    saveFitnessWorkout,
    deleteFitnessWorkout,
    saveFitnessBodyweight,
    deleteFitnessBodyweight,
    saveFitnessSettings,
    loadJournalEntries,
    saveJournalEntry,
    deleteJournalEntry,
    getJournalEntriesByDate,
    getJournalEntriesByDateRange,
    preferences,
    loadPreferences,
    savePreferences,
    stats,
    loadStats,
    saveStats,
    recomputeStreak,
    refreshAll,
    exportData,
    importData,
    switchProvider,
    clearAllData,
    readRawFile,
    writeRawFile,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
